import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import Tooltip from '@mui/material/Tooltip';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../../../../../Config/firebase';
import { SECRETS_ENV } from '../../../../../Config/firebase';

const API_ENDPOINT = SECRETS_ENV.API_END_POINT;

// Define the GoogleMeeting type
export interface GoogleMeeting {
    id: string;
    title: string;
    meetLink: string;
    description: string;
    scheduledTime: number; // timestamp
    duration: number; // in minutes
    createdAt: number;
    weekDays?: number[]; // 0-6 for Sunday-Saturday
    type: 'one-time' | 'recurring';
    eventId?: string; // Google Calendar event ID
    attendeeCount?: number;
    recurring?: boolean;
    recurringDays?: number[];
}

interface GoogleMeetSectionProps {
    serviceProviderId: string;
    subscriptionId: string;
    googleMeetings?: GoogleMeeting[]; // Keep for backward compatibility
    subscriptionName: string;
}

export default function GoogleMeetSection({ 
    serviceProviderId, 
    subscriptionId, 
    // googleMeetings prop is no longer used since we're using Firestore directly
    subscriptionName 
}: GoogleMeetSectionProps) {
    const location = useLocation();
    
    // State to store meetings from subcollection
    const [meetings, setMeetings] = useState<GoogleMeeting[]>([]);
    const [isLoadingMeetings, setIsLoadingMeetings] = useState(false);
    
    // State for the form
    const [showForm, setShowForm] = useState(false);
    const [editingMeetingId, setEditingMeetingId] = useState<string | null>(null);
    const [formData, setFormData] = useState<Omit<GoogleMeeting, 'id' | 'createdAt'>>({
        title: '',
        meetLink: '',
        description: '',
        scheduledTime: Date.now(),
        duration: 60,
        type: 'one-time',
        weekDays: []
    });
    const [isCreatingMeeting, setIsCreatingMeeting] = useState(false);
    const [isGoogleAuthenticated, setIsGoogleAuthenticated] = useState(false);
    
    // State for Google auth modal
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [authLoading, setAuthLoading] = useState(false);
    const [authSuccess, setAuthSuccess] = useState(false);
    
    // State for unlink confirmation modal
    const [showUnlinkModal, setShowUnlinkModal] = useState(false);
    const [unlinkLoading, setUnlinkLoading] = useState(false);

    // Add these state declarations at the top of your component with other state declarations
    const [showCalendarDropdown, setShowCalendarDropdown] = useState<string | null>(null);
    const [showShareDropdown, setShowShareDropdown] = useState<string | null>(null);

    // Add a state to track which meeting is being deleted
    const [deletingMeetingId, setDeletingMeetingId] = useState<string | null>(null);

    // Add this state to store the Google account email
    const [connectedGoogleEmail, setConnectedGoogleEmail] = useState<string | null>(null);

    // Add caching for auth status
    const [lastAuthCheck, setLastAuthCheck] = useState<number>(0);
    const AUTH_CACHE_DURATION = 10 * 60 * 1000; // 10 minutes in milliseconds (increased from 5 minutes)

    // Backend now stores Google tokens in: ServiceProviders/{userId}/MeetingProviders/google
    // Expected API response should include: { isAuthenticated, email, providerEmail, name, displayName, firstName, lastName, etc. }
    const checkGoogleAuth = async (forceCheck: boolean = false) => {
        try {
            if (!serviceProviderId) return;
            
            // Check cache unless forced
            const now = Date.now();
            if (!forceCheck && (now - lastAuthCheck) < AUTH_CACHE_DURATION) {
                console.log('Using cached Google auth status');
                return;
            }
            
            console.log('Checking Google Meet auth status for user:', serviceProviderId);
            const response = await axios.post(`${API_ENDPOINT}/api/gmeet/check-auth`, {
                userId: serviceProviderId
            });
            
            const wasAuthenticated = isGoogleAuthenticated;
            const authData = response.data;
            
            setIsGoogleAuthenticated(authData.isAuthenticated);
            setLastAuthCheck(now);
            
            // Store the email and name if available in the response
            if (authData.email) {
                setConnectedGoogleEmail(authData.email);
            }
            
            console.log('GoogleMeetSection - Google Meet auth check result:', {
                isAuthenticated: authData.isAuthenticated,
                email: authData.email,
                name: authData.name,
                fullResponse: authData
            });
            
            // If newly authenticated, show success
            if (!wasAuthenticated && authData.isAuthenticated) {
                setAuthSuccess(true);
                if (authData.email || authData.name) {
                    const displayName = authData.name || authData.email;
                    toast.success(`Connected to Google Meet as ${displayName}`);
                }
            }
        } catch (error) {
            console.error('Error checking Google auth:', error);
            setIsGoogleAuthenticated(false);
            setConnectedGoogleEmail(null);
            setLastAuthCheck(0); // Reset cache on error
        }
    };

    // Fetch meetings from subcollection
    useEffect(() => {
        if (!serviceProviderId || !subscriptionId) return;
        
        setIsLoadingMeetings(true);
        
        const meetingsRef = collection(
            db, 
            'ServiceProviders', serviceProviderId, 
            'Subscriptions', subscriptionId, 
            'Meetings'
        );
        
        // Use onSnapshot to get real-time updates
        const unsubscribe = onSnapshot(meetingsRef, (snapshot) => {
            const meetingsData: GoogleMeeting[] = [];
            
            snapshot.forEach((doc) => {
                const data = doc.data();
                
                // Only include meetings that are Google Meet type
                if (data.type !== 'GMEET') {
                    return; // Skip this meeting if it's not a Google Meet
                }
                
                // Convert Firestore timestamps to numbers
                const scheduledTime = data.startTime?.toDate().getTime() || 0;
                
                // Map the Firestore data to the GoogleMeeting type
                meetingsData.push({
                    id: doc.id,
                    title: data.title || '',
                    meetLink: data.meetLink || '',
                    description: data.description || '',
                    scheduledTime: scheduledTime,
                    duration: (data.endTime?.toDate().getTime() - scheduledTime) / 60000 || 60, // Calculate duration in minutes
                    createdAt: data.createdAt?.toDate().getTime() || Date.now(),
                    type: data.recurring ? 'recurring' : 'one-time',
                    weekDays: data.recurringDays || [],
                    eventId: data.eventId,
                    attendeeCount: data.attendeeCount || 0,
                    recurring: data.recurring || false,
                    recurringDays: data.recurringDays || []
                });
            });
            
            // Sort meetings by scheduled time (newest first)
            meetingsData.sort((a, b) => b.scheduledTime - a.scheduledTime);
            
            setMeetings(meetingsData);
            setIsLoadingMeetings(false);
        }, (error) => {
            console.error('Error fetching meetings:', error);
            toast.error('Failed to load meetings');
            setIsLoadingMeetings(false);
        });
        
        // Clean up subscription
        return () => unsubscribe();
    }, [serviceProviderId, subscriptionId]);

    // Update useEffect to handle auth state and modal
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        if (searchParams.get('googleAuthSuccess') === 'true') {
            // Show the modal if not already shown
            setShowAuthModal(true);
            setAuthSuccess(true);
            
            // Remove the query parameter without page refresh
            const newUrl = window.location.pathname;
            window.history.replaceState({}, document.title, newUrl);
            
            // Force check auth status (bypass cache)
            checkGoogleAuth(true);
        }
    }, [location.search]);

    // Check if user is authenticated with Google when component mounts
    useEffect(() => {
        // Only check auth if we don't have a recent check (use the cache)
        const shouldCheck = !lastAuthCheck || (Date.now() - lastAuthCheck) >= AUTH_CACHE_DURATION;
        if (shouldCheck && serviceProviderId) {
        checkGoogleAuth();
        }
    }, [serviceProviderId]); // Only re-run when serviceProviderId changes

    // Format date for display
    const formatDate = (timestamp: number) => {
        const date = new Date(timestamp);
        return date.toLocaleString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    // Format date for datetime-local input
    const formatDateForInput = (timestamp: number) => {
        const date = new Date(timestamp);
        return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
            .toISOString()
            .slice(0, 16);
    };

    // Handle form input changes
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'duration' ? parseInt(value) || 0 : value
        }));
    };

    // Handle date and time input
    const handleDateTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const dateTimeValue = new Date(e.target.value).getTime();
        setFormData(prev => ({
            ...prev,
            scheduledTime: dateTimeValue
        }));
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            title: '',
            meetLink: '',
            description: '',
            scheduledTime: Date.now(),
            duration: 60,
            type: 'one-time',
            weekDays: []
        });
        setEditingMeetingId(null);
        setShowForm(false);
    };

    // Update handleGoogleAuth
    const handleGoogleAuth = async () => {
        try {
            setAuthLoading(true);
            
            if (!serviceProviderId) {
                toast.error('Service Provider ID is required for authentication');
                setAuthLoading(false);
                return;
            }
            
            // Keep the modal open during redirect
            setShowAuthModal(true);
            
            // Get the current URL as redirect URL
            const redirectUrl = `${window.location.origin}${window.location.pathname}`;
            const response = await axios.post(`${API_ENDPOINT}/api/gmeet/get-auth-url`, {
                userId: serviceProviderId,
                redirectUrl: encodeURIComponent(redirectUrl)
            });
            
            window.location.href = response.data.authUrl;
        } catch (error) {
            console.error('Error getting auth URL:', error);
            toast.error('Failed to start Google authentication');
            setAuthLoading(false);
            setShowAuthModal(false);
        }
    };

    // Handle unlinking Google account
    const handleUnlinkGoogle = async () => {
        try {
            setUnlinkLoading(true);
            
            if (!serviceProviderId) {
                toast.error('Service Provider ID is required');
                setUnlinkLoading(false);
                return;
            }
            
            await axios.post(`${API_ENDPOINT}/api/gmeet/revoke-access`, {
                userId: serviceProviderId
            });
            
            setIsGoogleAuthenticated(false);
            setShowUnlinkModal(false);
            toast.success('Google account unlinked successfully');
            
        } catch (error) {
            console.error('Error unlinking Google account:', error);
            toast.error('Failed to unlink Google account');
        } finally {
            setUnlinkLoading(false);
        }
    };

    // Create Google Meet via API
    const createGoogleMeetEvent = async () => {
        try {
            setIsCreatingMeeting(true);
            
            // Check if authenticated with Google
            if (!isGoogleAuthenticated) {
                toast.info('Please authenticate with Google first');
                handleGoogleAuth();
                setIsCreatingMeeting(false);
                return;
            }
            
            // Call API to create meeting
            const response = await axios.post(`${API_ENDPOINT}/api/gmeet/create-meeting`, {
                userId: serviceProviderId,
                title: formData.title,
                description: formData.description,
                subId: subscriptionId,
                startTime: new Date(formData.scheduledTime).toISOString(),
                duration: formData.duration,
                weekDays: formData.weekDays,
                timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
            });
            
            // Extract the Google Meet link from the response
            const { meetLink } = response.data;
            
            if (!meetLink) {
                throw new Error('No Google Meet link was generated');
            }
            
            // We don't need to update Redux anymore as we're using Firestore directly
            // The onSnapshot listener will update our local state automatically
            
            toast.success('Meeting created successfully');
            resetForm();
            
        } catch (error) {
            console.error('Error creating Google Meet:', error);
            toast.error('Failed to create Google Meet');
        } finally {
            setIsCreatingMeeting(false);
        }
    };

    // Handle form submission - modified to update Firestore directly
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        
        if (formData.type === 'recurring' && (!formData.weekDays || formData.weekDays.length === 0)) {
            toast.error('Please select at least one day for recurring meetings');
            return;
        }
        
        if (editingMeetingId) {
            // Update existing meeting via API
            const meeting = meetings.find(m => m.id === editingMeetingId);
            if (!meeting || !meeting.eventId) {
                toast.error('Meeting information is incomplete');
                resetForm();
                return;
            }
            
            // Call API to update meeting in Calendar and Firestore
            axios.post(`${API_ENDPOINT}/api/gmeet/update-meeting`, {
                userId: serviceProviderId,
                subId: subscriptionId,
                eventId: meeting.eventId,
                title: formData.title,
                description: formData.description,
                startTime: new Date(formData.scheduledTime).toISOString(),
                duration: formData.duration,
                weekDays: formData.weekDays,
                timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
            })
            .then(() => {
            toast.success('Meeting updated successfully');
            resetForm();
            })
            .catch(error => {
                console.error('Error updating meeting:', error);
                toast.error('Failed to update meeting');
            });
        } else {
            // Create new meeting
            createGoogleMeetEvent();
        }
    };

    // Handle editing a meeting
    const handleEditMeeting = (meeting: GoogleMeeting) => {
        setFormData({
            title: meeting.title,
            meetLink: meeting.meetLink,
            description: meeting.description,
            scheduledTime: meeting.scheduledTime,
            duration: meeting.duration,
            type: meeting.type,
            weekDays: meeting.weekDays || []
        });
        setEditingMeetingId(meeting.id);
        setShowForm(true);
    };

    // Handle deleting a meeting
    const handleDeleteMeeting = async (meetingId: string) => {
        try {
            // Set the deleting state to show loading
            setDeletingMeetingId(meetingId);
            
            const meeting = meetings.find(m => m.id === meetingId);
            if (!meeting || !meeting.eventId) {
                toast.error('Meeting information is incomplete');
                setDeletingMeetingId(null);
                return;
            }
            
            const response = await axios.post(`${API_ENDPOINT}/api/gmeet/delete-meeting`, {
                userId: serviceProviderId,
                subId: subscriptionId,
                eventId: meeting.eventId
            });
            
            // Check the actual response structure from your API
            if (response.status === 200) {
                // We received a response, check what happened
                const { message, calendarEventDeleted } = response.data;
                
                if (calendarEventDeleted) {
                    toast.success(message || 'Meeting deleted successfully');
                    // The onSnapshot listener should update the UI automatically
                } else {
                    // Calendar event wasn't deleted but the request succeeded
                    toast.warning('Meeting removed from calendar, but some cleanup may be needed');
                }
            } else {
                toast.error('Failed to delete meeting');
            }
        } catch (error) {
            console.error('Error deleting meeting:', error);
            toast.error('Failed to delete meeting');
        } finally {
            // Clear the deleting state regardless of the outcome
            setDeletingMeetingId(null);
        }
    };

    // Generate calendar event links
    const generateCalendarEvent = (meeting: GoogleMeeting) => {
        const startTime = new Date(meeting.scheduledTime);
        const endTime = new Date(meeting.scheduledTime + meeting.duration * 60 * 1000);
        
        const event = {
            title: meeting.title,
            description: `${meeting.description}\n\nJoin meeting: ${meeting.meetLink}`,
            startTime: startTime.toISOString(),
            endTime: endTime.toISOString(),
            location: meeting.meetLink
        };

        // Google Calendar link with recurrence
        let googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.title)}&details=${encodeURIComponent(event.description)}&location=${encodeURIComponent(event.location)}&dates=${event.startTime.replace(/[-:]/g, '')}/${event.endTime.replace(/[-:]/g, '')}`;
        
        // Add recurrence rule for recurring meetings
        if (meeting.type === 'recurring' && meeting.weekDays && meeting.weekDays.length > 0) {
            const weekDays = meeting.weekDays.map(day => {
                const days = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
                return days[day];
            }).join(',');
            googleCalendarUrl += `&recur=RRULE:FREQ=WEEKLY;BYDAY=${weekDays}`;
        }

        // iCal/Outlook format with proper formatting and recurrence
        let icsContent = [
            'BEGIN:VCALENDAR',
            'VERSION:2.0',
            'PRODID:-//CoachPro//Google Meet Integration//EN',
            'CALSCALE:GREGORIAN',
            'METHOD:PUBLISH',
            'BEGIN:VEVENT',
            `UID:${meeting.id}@coachpro.com`,
            `DTSTAMP:${new Date().toISOString().replace(/[-:]/g, '').split('.')[0]}Z`,
            `DTSTART:${event.startTime.replace(/[-:]/g, '').split('.')[0]}Z`,
            `DTEND:${event.endTime.replace(/[-:]/g, '').split('.')[0]}Z`,
            `SUMMARY:${event.title}`,
            `DESCRIPTION:${event.description.replace(/\n/g, '\\n')}`,
            `LOCATION:${event.location}`,
            `URL:${event.location}`
        ];

        // Add recurrence rule for recurring meetings
        if (meeting.type === 'recurring' && meeting.weekDays && meeting.weekDays.length > 0) {
            const weekDays = meeting.weekDays.map(day => {
                const days = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
                return days[day];
            }).join(',');
            icsContent.push(`RRULE:FREQ=WEEKLY;BYDAY=${weekDays}`);
        }

        // Add required end tags
        icsContent = icsContent.concat([
            'END:VEVENT',
            'END:VCALENDAR'
        ]);

        // Join with proper line endings (CRLF as per RFC 5545)
        const icsString = icsContent.join('\r\n');
        const icsFile = new Blob([icsString], { type: 'text/calendar;charset=utf-8' });
        const icsUrl = URL.createObjectURL(icsFile);

        return {
            googleCalendarUrl,
            icsUrl
        };
    };

    const shareToWhatsApp = (meeting: GoogleMeeting) => {
        const text = `Join my Google Meet: ${meeting.title}\n\nTime: ${formatDate(meeting.scheduledTime)}\nDuration: ${meeting.duration} minutes\n\nJoin using this link: ${meeting.meetLink}`;
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
        window.open(whatsappUrl, '_blank');
    };

    return (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="mb-4">
                <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                        <h3 className="text-lg font-medium text-gray-900">
                            <div className="flex items-center gap-2">
                                Google Meet
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                    Beta
                                </span>
                            </div>
                        </h3>
                    </div>
                    <div className="flex space-x-2">
                        {/* Google Account Status/Link Button */}
                        {!isGoogleAuthenticated ? (
                            <button
                                type="button"
                                onClick={() => setShowAuthModal(true)}
                                className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cc_brand"
                            >
                                <svg className="mr-1.5 h-4 w-4 text-[#4285F4]" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z"/>
                                </svg>
                                Link Google Account
                            </button>
                        ) : (
                            <div className="inline-flex items-center">
                                <div className="px-3 py-1.5 text-xs font-medium text-green-800 bg-green-100 rounded-md flex items-center">
                                    <svg className="mr-1.5 h-3 w-3 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    <span>
                                        Google Connected
                                        {connectedGoogleEmail && (
                                            <span className="ml-1 font-normal text-gray-600">
                                                ({connectedGoogleEmail})
                                            </span>
                                        )}
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowUnlinkModal(true)}
                                    className="ml-2 text-xs text-gray-500 hover:text-gray-700 underline"
                                >
                                    Unlink
                                </button>
                            </div>
                        )}
                        
                        {!showForm && (
                            <button
                                type="button"
                                onClick={() => setShowForm(true)}
                                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-cc_brand hover:bg-cc_brand_hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cc_brand"
                            >
                                <svg className="mr-1.5 h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                                Add Meeting
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Google Account Connection Banner (when not authenticated) */}
            {!isGoogleAuthenticated && !showForm && meetings.length === 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm text-blue-700">
                                Connect your Google account to create Google Meet meetings directly from this page.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Meeting Form */}
            {showForm && (
                <form onSubmit={handleSubmit} className="bg-gray-50 p-4 rounded-lg border border-gray-200 space-y-3">
                    <h4 className="font-medium text-gray-700">
                        {editingMeetingId ? 'Edit Meeting' : 'Create New Meeting'}
                    </h4>
                    
                    {/* Google Account Warning (when not authenticated) */}
                    {!isGoogleAuthenticated && !editingMeetingId && (
                        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm text-yellow-700">
                                        You need to connect your Google account to create Google Meet meetings.
                                        <button
                                            type="button"
                                            onClick={() => setShowAuthModal(true)}
                                            className="ml-2 font-medium text-yellow-700 underline hover:text-yellow-600"
                                        >
                                            Connect now
                                        </button>
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Meeting Title*
                        </label>
                        <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            placeholder={`${subscriptionName} Meeting`}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-cc_brand focus:border-cc_brand"
                            required
                        />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Meeting Type
                        </label>
                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, type: 'one-time', weekDays: [] }))}
                                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors
                                    ${formData.type === 'one-time'
                                        ? 'bg-cc_brand text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                            >
                                One-time Meeting
                            </button>
                            <button
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, type: 'recurring' }))}
                                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors
                                    ${formData.type === 'recurring'
                                        ? 'bg-cc_brand text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                            >
                                Recurring Meeting
                            </button>
                        </div>
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Date and Time*
                        </label>
                        <input
                            type="datetime-local"
                            value={formatDateForInput(formData.scheduledTime)}
                            onChange={handleDateTimeChange}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-cc_brand focus:border-cc_brand"
                            required
                        />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Duration (minutes)*
                        </label>
                        <input
                            type="number"
                            name="duration"
                            value={formData.duration}
                            onChange={handleInputChange}
                            min="15"
                            step="15"
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-cc_brand focus:border-cc_brand"
                            required
                        />
                    </div>
                    
                    {/* Only show weekday selection for recurring meetings */}
                    {formData.type === 'recurring' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Recurring Days*
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {[
                                    { day: 'Sun', value: 0 },
                                    { day: 'Mon', value: 1 },
                                    { day: 'Tue', value: 2 },
                                    { day: 'Wed', value: 3 },
                                    { day: 'Thu', value: 4 },
                                    { day: 'Fri', value: 5 },
                                    { day: 'Sat', value: 6 }
                                ].map(({ day, value }) => (
                                    <button
                                        key={value}
                                        type="button"
                                        onClick={() => {
                                            setFormData(prev => ({
                                                ...prev,
                                                weekDays: prev.weekDays?.includes(value)
                                                    ? prev.weekDays.filter(d => d !== value)
                                                    : [...(prev.weekDays || []), value]
                                            }));
                                        }}
                                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors
                                            ${formData.weekDays?.includes(value)
                                                ? 'bg-cc_brand text-white'
                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                            }`}
                                    >
                                        {day}
                                    </button>
                                ))}
                            </div>
                            {formData.weekDays?.length === 0 && (
                                <p className="mt-1 text-sm text-gray-500">
                                    Select days when this meeting will occur
                                </p>
                            )}
                        </div>
                    )}
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description
                        </label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            rows={3}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-cc_brand focus:border-cc_brand"
                        />
                    </div>
                    
                    <div className="flex justify-end space-x-2 pt-2">
                        <button
                            type="button"
                            onClick={resetForm}
                            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cc_brand"
                        >
                            Cancel
                        </button>
                        
                        {editingMeetingId ? (
                            <button
                                type="submit"
                                className="px-4 py-2 bg-cc_brand text-white rounded-md hover:bg-cc_brand_hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cc_brand"
                            >
                                Update Meeting
                            </button>
                        ) : (
                            <button
                                type="submit"
                                disabled={isCreatingMeeting || (!isGoogleAuthenticated && !editingMeetingId)}
                                className="px-4 py-2 bg-cc_brand text-white rounded-md hover:bg-cc_brand_hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cc_brand flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                                {isCreatingMeeting ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                                        </svg>
                                        Create Google Meet
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </form>
            )}

            {/* Meeting List */}
            {isLoadingMeetings ? (
                <div className="py-8 text-center">
                    <CircularProgress size={30} className="mb-2" />
                    <p className="text-sm text-gray-500">Loading meetings...</p>
                    </div>
            ) : meetings.length === 0 ? (
                <div className="py-8 text-center text-gray-500">
                    <p>No meetings scheduled yet.</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {meetings
                        .map(meeting => (
                            <div 
                                key={meeting.id} 
                                className="bg-white p-4 rounded-lg border border-gray-200 hover:shadow-md transition-shadow"
                            >
                                <div className="flex justify-between items-start">
                                    <div>
                                        <div className="flex items-center gap-2">
                                        <h4 className="font-medium text-gray-900">{meeting.title}</h4>
                                            {meeting.recurring && (
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                    <svg className="mr-1 h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                                        <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                                                    </svg>
                                                    Recurring
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-sm text-gray-500 mt-1">
                                            {formatDate(meeting.scheduledTime)} • {meeting.duration} minutes
                                        </p>
                                        {meeting.description && (
                                            <p className="text-sm text-gray-700 mt-2">{meeting.description}</p>
                                        )}
                                    </div>
                                    
                                    <div className="flex items-center gap-2">
                                        <Tooltip title="Edit Meeting" arrow>
                                            <button
                                                onClick={() => handleEditMeeting(meeting)}
                                                className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500 hover:text-cc_brand transition-colors"
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                                </svg>
                                            </button>
                                        </Tooltip>
                                        <Tooltip title="Delete Meeting" arrow>
                                            <button
                                                onClick={() => handleDeleteMeeting(meeting.id)}
                                                disabled={deletingMeetingId === meeting.id}
                                                className="p-1.5 rounded-full hover:bg-red-50 text-gray-500 hover:text-red-500 transition-colors disabled:opacity-50"
                                            >
                                                {deletingMeetingId === meeting.id ? (
                                                    <div className="w-4 h-4 animate-spin rounded-full border-2 border-gray-300 border-t-red-500" />
                                                ) : (
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <polyline points="3 6 5 6 21 6"></polyline>
                                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                    </svg>
                                                )}
                                            </button>
                                        </Tooltip>
                                    </div>
                                </div>
                                
                                <div className="mt-3 flex items-center gap-4">
                                    <a 
                                        href={meeting.meetLink} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center text-sm text-cc_brand hover:text-cc_brand_hover"
                                    >
                                        <svg className="mr-1" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                        Join Meeting
                                    </a>
                                    
                                    <div className="relative">
                                    <Tooltip title="Add to Calendar" arrow>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setShowCalendarDropdown(showCalendarDropdown === meeting.id ? null : meeting.id);
                                                    setShowShareDropdown(null);
                                                }}
                                                className="p-2 rounded-full hover:bg-gray-100 text-gray-500 hover:text-cc_brand transition-colors"
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                                    <line x1="16" y1="2" x2="16" y2="6" />
                                                    <line x1="8" y1="2" x2="8" y2="6" />
                                                    <line x1="3" y1="10" x2="21" y2="10" />
                                                </svg>
                                            </button>
                                        </Tooltip>
                                        {showCalendarDropdown === meeting.id && (
                                            <div 
                                                className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10"
                                                style={{ minWidth: '200px' }}
                                            >
                                                <a
                                                    href={generateCalendarEvent(meeting).googleCalendarUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                    onClick={() => setShowCalendarDropdown(null)}
                                                >
                                                    Add to Google Calendar
                                                </a>
                                                <a
                                                    href={generateCalendarEvent(meeting).icsUrl}
                                                    download={`${meeting.title}.ics`}
                                                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                    onClick={() => setShowCalendarDropdown(null)}
                                                >
                                                    Download for Outlook/iCal
                                                </a>
                                            </div>
                                        )}
                                        </div>

                                    <div className="relative">
                                    <Tooltip title="Share Meeting" arrow>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setShowShareDropdown(showShareDropdown === meeting.id ? null : meeting.id);
                                                    setShowCalendarDropdown(null);
                                                }}
                                                className="p-2 rounded-full hover:bg-gray-100 text-gray-500 hover:text-cc_brand transition-colors"
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <circle cx="18" cy="5" r="3" />
                                                    <circle cx="6" cy="12" r="3" />
                                                    <circle cx="18" cy="19" r="3" />
                                                    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                                                    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                                                </svg>
                                            </button>
                                        </Tooltip>
                                        {showShareDropdown === meeting.id && (
                                            <div 
                                                className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10"
                                                style={{ minWidth: '200px' }}
                                            >
                                                <button
                                                    onClick={() => {
                                                        shareToWhatsApp(meeting);
                                                        setShowShareDropdown(null);
                                                    }}
                                                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                >
                                                    Share via WhatsApp
                                                </button>
                                    <button
                                        onClick={() => {
                                            navigator.clipboard.writeText(meeting.meetLink);
                                            toast.success('Meeting link copied to clipboard');
                                                        setShowShareDropdown(null);
                                                    }}
                                                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                >
                                                    Copy Meeting Link
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        const text = `Join my Google Meet: ${meeting.title}\nTime: ${formatDate(meeting.scheduledTime)}\nDuration: ${meeting.duration} minutes\n\nJoin using this link: ${meeting.meetLink}`;
                                                        navigator.clipboard.writeText(text);
                                                        toast.success('Meeting details copied to clipboard');
                                                        setShowShareDropdown(null);
                                                    }}
                                                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                >
                                                    Copy Meeting Details
                                    </button>
                                </div>
                                        )}
                            </div>
                                </div>
                            </div>
                        ))
                    }
                </div>
            )}

            {/* Google Auth Modal */}
            <Modal
                open={showAuthModal}
                onClose={() => !authLoading && setShowAuthModal(false)}
                aria-labelledby="google-auth-modal-title"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: 24,
                    p: 4,
                }}>
                    <div className="text-center">
                        <h2 id="google-auth-modal-title" className="text-xl font-semibold mb-4">
                            Connect Google Account
                        </h2>
                        
                        {authSuccess ? (
                            <div className="py-6">
                                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                                    <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <div className="mt-3">
                                    <h3 className="text-lg font-medium text-gray-900">Connection Successful!</h3>
                                    <p className="mt-2 text-sm text-gray-500">
                                        Your Google account 
                                        {connectedGoogleEmail ? ` (${connectedGoogleEmail})` : ''} 
                                        has been successfully connected. You can now create Google Meet meetings.
                                    </p>
                                    <div className="mt-4">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setShowAuthModal(false);
                                                setAuthSuccess(false);
                                            }}
                                            className="inline-flex justify-center w-full rounded-md border border-transparent shadow-sm px-4 py-2 bg-cc_brand text-base font-medium text-white hover:bg-cc_brand_hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cc_brand sm:text-sm"
                                        >
                                            Continue
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="mb-6">
                                    <p className="text-sm text-gray-500">
                                        Connect your Google account to create and manage Google Meet meetings directly from this application.
                                    </p>
                                </div>
                                
                                {authLoading ? (
                                    <div className="py-6">
                                        <CircularProgress size={40} className="mb-4" />
                                        <p className="text-sm text-gray-500">
                                            Waiting for Google authentication...
                                        </p>
                                    </div>
                                ) : (
                                    <button
                                        onClick={handleGoogleAuth}
                                        className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-[#4285F4] hover:bg-[#3367d6] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4285F4]"
                                    >
                                        <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z"/>
                                        </svg>
                                        Connect with Google
                                    </button>
                                )}
                                
                                <div className="mt-4">
                                    <button
                                        type="button"
                                        onClick={() => setShowAuthModal(false)}
                                        disabled={authLoading}
                                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </Box>
            </Modal>

            {/* Unlink Google Account Modal */}
            <Modal
                open={showUnlinkModal}
                onClose={() => !unlinkLoading && setShowUnlinkModal(false)}
                aria-labelledby="unlink-google-modal-title"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: 24,
                    p: 4,
                }}>
                    <div>
                        <h2 id="unlink-google-modal-title" className="text-xl font-semibold mb-4">
                            Unlink Google Account
                        </h2>
                        
                        <div className="mb-6">
                            <p className="text-sm text-gray-500">
                                Are you sure you want to unlink your Google account
                                {connectedGoogleEmail ? ` (${connectedGoogleEmail})` : ''}? 
                                This will prevent you from creating new Google Meet meetings.
                                {meetings.length > 0 && (
                                    <span className="block mt-2 text-yellow-600 font-medium">
                                        Note: Your existing meetings will remain accessible.
                                    </span>
                                )}
                            </p>
                        </div>
                        
                        <div className="flex justify-end space-x-3">
                            <button
                                type="button"
                                onClick={() => setShowUnlinkModal(false)}
                                disabled={unlinkLoading}
                                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cc_brand disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            
                            <button
                                type="button"
                                onClick={handleUnlinkGoogle}
                                disabled={unlinkLoading}
                                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center disabled:opacity-70"
                            >
                                {unlinkLoading ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Unlinking...
                                    </>
                                ) : (
                                    'Unlink Account'
                                )}
                            </button>
                        </div>
                    </div>
                </Box>
            </Modal>
        </div>
    );
}